import * as React from 'react';
import {
  FortumLogo,
  IconFortum,
  IconMenu,
  IconButton,
  breakpoints,
  colors,
} from '@fortum/elemental-ui';
import { Language } from '@configs/i18n';
import { UserInfo } from '@models/userInfo';
import { Namespace, useTranslation } from 'react-i18next';
import { useResponsiveWidth } from '@utils/useResponsiveWidth';
import { AuthWidget } from './AuthWidget';
import { AppSidebar } from './AppSidebar';
import {
  DesktopBrandContainer,
  DesktopBrandText,
  DesktopHeaderShell,
  DesktopLogoLink,
  DesktopNavigation,
  DesktopNavButton,
  DesktopSecondRow,
  DesktopSeparator,
  DesktopTopRow,
  DesktopTopRowActions,
  StyledHeader,
  RightComponentsContainer,
  LogoContainer,
  ContainerSidebar,
  MobileBrandContainer,
  MobileBrandText,
  TabletBrandText,
  MobileSeparator,
  TabletSeparator,
  FlexLogoContainer,
  NavSpacer,
  StyledLogoLink,
  TabletLogoLink,
  MobileLogoLink,
} from './styles';
import { LanguageMenu } from './LanguageMenu';

interface AppHeaderProps {
  text: string;
  currentLanguage: Language;
  onLanguageSelect: (language: string) => void;
  languages: Language[];
  userName: string;
  customerType: UserInfo['customerType'];
  navItems?: Array<{ id: string | number; name: string }>;
  onNavSelect?: (tabId: string | number) => void;
  activeNavItem?: string;
}

export const AppHeader: React.FC<AppHeaderProps> = ({
  text,
  currentLanguage,
  onLanguageSelect,
  languages,
  userName,
  customerType,
  navItems,
  onNavSelect,
  activeNavItem,
}) => {
  const { t } = useTranslation<Namespace>('glossary');
  const responsiveWidth = useResponsiveWidth();
  const isDesktop = responsiveWidth >= breakpoints.l;
  const isTrueMobile = responsiveWidth < breakpoints.s;
  const [sidebarVisible, setSidebarVisible] = React.useState(false);
  const showSidebar = () => setSidebarVisible(true);
  const hideSidebar = () => setSidebarVisible(false);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  };

  return (
    <>
      {isDesktop ? (
        <DesktopHeaderShell>
          <DesktopTopRow>
            <DesktopTopRowActions>
              <LanguageMenu
                languages={languages}
                selected={currentLanguage}
                onSelect={onLanguageSelect}
                forceCondensed
                triggerColor={colors.ivyGreen600}
                transparent
              />
              <AuthWidget
                userName={userName}
                forceCondensed
                triggerColor={colors.ivyGreen600}
                transparent
              />
            </DesktopTopRowActions>
          </DesktopTopRow>
          <DesktopSecondRow>
            <DesktopBrandContainer>
              <DesktopLogoLink href="/" onClick={scrollToTop}>
                <FortumLogo size="s" color={colors.snowWhite} p={0} />
              </DesktopLogoLink>
              <DesktopSeparator />
              <DesktopBrandText level={2}>Heat Online</DesktopBrandText>
            </DesktopBrandContainer>
            {!!navItems?.length && (
              <nav aria-label={t('main.navigation')}>
                <DesktopNavigation>
                  {navItems.map(item => {
                    const isActive = String(activeNavItem) === String(item.id);

                    return (
                      <DesktopNavButton
                        key={String(item.id)}
                        status="plain"
                        invert
                        variant="condensed"
                        $active={isActive}
                        onClick={() => onNavSelect?.(item.id)}
                        aria-current={isActive ? 'page' : undefined}
                      >
                        {item.name}
                      </DesktopNavButton>
                    );
                  })}
                </DesktopNavigation>
              </nav>
            )}
          </DesktopSecondRow>
        </DesktopHeaderShell>
      ) : (
        <StyledHeader>
          <FlexLogoContainer>
            <ContainerSidebar>
              <IconButton
                status="plain"
                icon={<IconMenu />}
                aria-label={t('toggle.main.menu')}
                aria-expanded={sidebarVisible}
                onClick={showSidebar}
                color={colors.ivyGreen600}
              />
            </ContainerSidebar>
            <LogoContainer>
              <StyledLogoLink href="/" onClick={scrollToTop}>
                <MobileBrandContainer>
                  {isTrueMobile ? (
                    <MobileLogoLink>
                      <IconFortum color={colors.fullBlack} />
                    </MobileLogoLink>
                  ) : (
                    <TabletLogoLink>
                      <FortumLogo size="s" color={colors.ivyGreen900} p={0} />
                    </TabletLogoLink>
                  )}

                  {isTrueMobile ? <MobileSeparator /> : <TabletSeparator />}
                  {isTrueMobile ? (
                    <MobileBrandText level={2} weight="medium">
                      Heat Online
                    </MobileBrandText>
                  ) : (
                    <TabletBrandText level={2} weight="medium">
                      Heat Online
                    </TabletBrandText>
                  )}
                </MobileBrandContainer>
              </StyledLogoLink>
            </LogoContainer>
          </FlexLogoContainer>
          <RightComponentsContainer>
            <LanguageMenu
              languages={languages}
              selected={currentLanguage}
              onSelect={onLanguageSelect}
              triggerColor={colors.ivyGreen600}
            />
            <AuthWidget userName={userName} triggerColor={colors.ivyGreen600} />
          </RightComponentsContainer>
        </StyledHeader>
      )}
      <NavSpacer />
      <AppSidebar visible={sidebarVisible} onHide={hideSidebar} text={text} />
    </>
  );
};
